/**
 * Status Group Rules Engine
 *
 * This module provides infrastructure for defining and evaluating rules
 * based on status groups. Rules can trigger visual indicators, notifications,
 * or automated actions based on affaire status and activity patterns.
 *
 * Example use cases:
 * - "Affaires in group 40 (Expert à visiter) with no activity for 2 days"
 * - "Affaires in group 90 (Attente paiement) with no activity for 7 days"
 * - "Affaires in group 20 (Bureau à planifier) created more than 48h ago"
 */

import type { TrackerChannel } from '../types/tracker.types';
import { getStatusGroupId } from '../constants/statusGroups.constants';

// Rule severity levels
export type RuleSeverity = 'info' | 'warning' | 'critical';

// Rule evaluation result
export interface RuleEvaluation {
  ruleId: string;
  triggered: boolean;
  severity: RuleSeverity;
  message: string;
  actionable?: boolean; // Whether the rule suggests a specific action
  suggestedAction?: string;
  metadata?: Record<string, any>;
}

// Rule condition types
export type RuleConditionType =
  | 'days_since_activity'
  | 'days_since_creation'
  | 'has_unread_tasks'
  | 'has_active_tasks'
  | 'is_subscribed'
  | 'is_active'
  | 'custom';

// Rule condition definition
export interface RuleCondition {
  type: RuleConditionType;
  operator: 'gt' | 'gte' | 'lt' | 'lte' | 'eq' | 'neq';
  value: any;
  customEvaluator?: (channel: TrackerChannel) => boolean;
}

// Rule definition
export interface StatusGroupRule {
  id: string;
  name: string;
  description: string;
  statusGroupIds: number[]; // Which status groups this rule applies to
  conditions: RuleCondition[];
  severity: RuleSeverity;
  message: string; // Message template (can use {placeholders})
  actionable?: boolean;
  suggestedAction?: string;
  enabled: boolean;
}

/**
 * Evaluate a single condition against a channel
 */
function evaluateCondition(condition: RuleCondition, channel: TrackerChannel): boolean {
  // Custom evaluator takes precedence
  if (condition.customEvaluator) {
    return condition.customEvaluator(channel);
  }

  let actualValue: any;

  // Get the actual value based on condition type
  switch (condition.type) {
    case 'days_since_activity':
      actualValue = channel.days_since_activity ?? null;
      break;

    case 'days_since_creation':
      if (channel.created_at) {
        const createdAt = new Date(channel.created_at);
        const now = new Date();
        actualValue = Math.floor((now.getTime() - createdAt.getTime()) / (1000 * 60 * 60 * 24));
      } else {
        actualValue = null;
      }
      break;

    case 'has_unread_tasks':
      actualValue = (channel.total_unread_count ?? channel.unread_task_count) > 0;
      break;

    case 'has_active_tasks':
      actualValue = channel.active_task_count > 0;
      break;

    case 'is_subscribed':
      actualValue = channel.is_subscribed;
      break;

    case 'is_active':
      actualValue = channel.is_active ?? true; // Default to active if not specified
      break;

    default:
      return false;
  }

  // Handle null values
  if (actualValue === null || actualValue === undefined) {
    return false;
  }

  // Apply operator
  switch (condition.operator) {
    case 'gt':
      return actualValue > condition.value;
    case 'gte':
      return actualValue >= condition.value;
    case 'lt':
      return actualValue < condition.value;
    case 'lte':
      return actualValue <= condition.value;
    case 'eq':
      return actualValue === condition.value;
    case 'neq':
      return actualValue !== condition.value;
    default:
      return false;
  }
}

/**
 * Evaluate a rule against a channel
 */
export function evaluateRule(rule: StatusGroupRule, channel: TrackerChannel): RuleEvaluation {
  // Check if rule is enabled
  if (!rule.enabled) {
    return {
      ruleId: rule.id,
      triggered: false,
      severity: rule.severity,
      message: '',
    };
  }

  // Check if channel's status group matches rule's target groups
  const channelGroupId = getStatusGroupId(channel.status);
  if (channelGroupId === undefined || !rule.statusGroupIds.includes(channelGroupId)) {
    return {
      ruleId: rule.id,
      triggered: false,
      severity: rule.severity,
      message: '',
    };
  }

  // Evaluate all conditions (AND logic)
  const allConditionsMet = rule.conditions.every(condition =>
    evaluateCondition(condition, channel)
  );

  if (!allConditionsMet) {
    return {
      ruleId: rule.id,
      triggered: false,
      severity: rule.severity,
      message: '',
    };
  }

  // Rule triggered - generate message with placeholder replacement
  let message = rule.message;
  message = message.replace('{affaire_id}', String(channel.affaire_id));
  message = message.replace('{days_since_activity}', String(channel.days_since_activity ?? 'N/A'));
  message = message.replace('{status}', channel.status || 'Unknown');

  return {
    ruleId: rule.id,
    triggered: true,
    severity: rule.severity,
    message,
    actionable: rule.actionable,
    suggestedAction: rule.suggestedAction,
    metadata: {
      statusGroupId: channelGroupId,
      affaireId: channel.affaire_id,
    },
  };
}

/**
 * Evaluate multiple rules against a channel
 */
export function evaluateRules(rules: StatusGroupRule[], channel: TrackerChannel): RuleEvaluation[] {
  return rules
    .map(rule => evaluateRule(rule, channel))
    .filter(evaluation => evaluation.triggered);
}

/**
 * Evaluate rules against multiple channels
 */
export function evaluateRulesForChannels(
  rules: StatusGroupRule[],
  channels: TrackerChannel[]
): Map<string, RuleEvaluation[]> {
  const results = new Map<string, RuleEvaluation[]>();

  for (const channel of channels) {
    const evaluations = evaluateRules(rules, channel);
    if (evaluations.length > 0) {
      results.set(channel.id, evaluations);
    }
  }

  return results;
}

// ============================================
// Predefined Rules
// ============================================

export const PREDEFINED_RULES: StatusGroupRule[] = [
  // Status Group 10 - Bureau à encoder - 1 jour
  {
    id: 'bureau-encoder-stale-1day',
    name: 'Bureau à encoder - Pas d\'activité 1 jour',
    description: 'Dossier nouvellement créé sans activité depuis 1 jour',
    statusGroupIds: [10],
    conditions: [
      { type: 'days_since_activity', operator: 'gte', value: 1 },
    ],
    severity: 'warning',
    message: 'Aucune activité depuis {days_since_activity} jour(s)',
    actionable: true,
    suggestedAction: 'Encoder le dossier',
    enabled: true,
  },

  // Status Group 20 - Bureau à planifier - 1 jour
  {
    id: 'bureau-planifier-stale-1day',
    name: 'Bureau à planifier - Pas d\'activité 1 jour',
    description: 'Affaire à planifier sans activité depuis 1 jour',
    statusGroupIds: [20],
    conditions: [
      { type: 'days_since_activity', operator: 'gte', value: 1 },
    ],
    severity: 'warning',
    message: 'Aucune activité depuis {days_since_activity} jour(s)',
    actionable: true,
    suggestedAction: 'Planifier un rendez-vous',
    enabled: true,
  },

  // Status Group 30 - RV en attente - 2 jours
  {
    id: 'rv-attente-stale-2days',
    name: 'RV en attente - Pas d\'activité 2 jours',
    description: 'Rendez-vous en attente de planification depuis 2+ jours',
    statusGroupIds: [30],
    conditions: [
      { type: 'days_since_activity', operator: 'gte', value: 2 },
    ],
    severity: 'warning',
    message: 'Aucune activité depuis {days_since_activity} jour(s)',
    actionable: true,
    suggestedAction: 'Vérifier le statut du rendez-vous',
    enabled: true,
  },

  // Status Group 40 - Expert à visiter - PAS DE LIMITE (disabled)
  {
    id: 'expert-visiter-no-limit',
    name: 'Expert à visiter - Pas de limite',
    description: 'Pas de limite de temps pour ce statut',
    statusGroupIds: [40],
    conditions: [],
    severity: 'info',
    message: '',
    actionable: false,
    enabled: false,
  },

  // Status Group 50 - Expert à achever - 2 jours
  {
    id: 'expert-achever-stale-2days',
    name: 'Expert à achever - Pas d\'activité 2 jours',
    description: 'Rapports d\'expertise à finaliser depuis 2+ jours',
    statusGroupIds: [50],
    conditions: [
      { type: 'days_since_activity', operator: 'gte', value: 2 },
    ],
    severity: 'warning',
    message: 'Aucune activité depuis {days_since_activity} jour(s)',
    actionable: true,
    suggestedAction: 'Contacter l\'expert pour finaliser',
    enabled: true,
  },

  // Status Group 60 - Bureau à valoriser - 2 jours
  {
    id: 'bureau-valoriser-stale-2days',
    name: 'Bureau à valoriser - Pas d\'activité 2 jours',
    description: 'Rapport à valoriser depuis 2+ jours',
    statusGroupIds: [60],
    conditions: [
      { type: 'days_since_activity', operator: 'gte', value: 2 },
    ],
    severity: 'warning',
    message: 'Aucune activité depuis {days_since_activity} jour(s)',
    actionable: true,
    suggestedAction: 'Valoriser le rapport',
    enabled: true,
  },

  // Status Group 70 - Bureau à achever - 2 jours
  {
    id: 'bureau-achever-stale-2days',
    name: 'Bureau à achever - Pas d\'activité 2 jours',
    description: 'Rapport à achever depuis 2+ jours',
    statusGroupIds: [70],
    conditions: [
      { type: 'days_since_activity', operator: 'gte', value: 2 },
    ],
    severity: 'warning',
    message: 'Aucune activité depuis {days_since_activity} jour(s)',
    actionable: true,
    suggestedAction: 'Finaliser le rapport',
    enabled: true,
  },

  // Status Group 80 - Attente doc/infos - PAS DE LIMITE (disabled)
  {
    id: 'attente-docs-no-limit',
    name: 'Attente doc/infos - Pas de limite',
    description: 'Pas de limite de temps pour ce statut',
    statusGroupIds: [80],
    conditions: [],
    severity: 'info',
    message: '',
    actionable: false,
    enabled: false,
  },

  // Status Group 90 - Attente paiement - PAS DE LIMITE (disabled)
  {
    id: 'attente-paiement-no-limit',
    name: 'Attente paiement - Pas de limite',
    description: 'Pas de limite de temps pour ce statut',
    statusGroupIds: [90],
    conditions: [],
    severity: 'info',
    message: '',
    actionable: false,
    enabled: false,
  },

  // Status Group 100 - Att. confirmation - PAS DE LIMITE (disabled)
  {
    id: 'attente-confirmation-no-limit',
    name: 'Att. confirmation - Pas de limite',
    description: 'Pas de limite de temps pour ce statut',
    statusGroupIds: [100],
    conditions: [],
    severity: 'info',
    message: '',
    actionable: false,
    enabled: false,
  },

  // Status Group 110 - À transmettre - 2 jours
  {
    id: 'a-transmettre-stale-2days',
    name: 'À transmettre - Pas d\'activité 2 jours',
    description: 'Dossier prêt à transmettre depuis 2+ jours',
    statusGroupIds: [110],
    conditions: [
      { type: 'days_since_activity', operator: 'gte', value: 2 },
    ],
    severity: 'warning',
    message: 'Aucune activité depuis {days_since_activity} jour(s)',
    actionable: true,
    suggestedAction: 'Transmettre le dossier',
    enabled: true,
  },

  // Status Group 999 - Autres - PAS DE LIMITE (disabled)
  {
    id: 'autres-no-limit',
    name: 'Autres - Pas de limite',
    description: 'Pas de limite de temps pour archives et autres statuts',
    statusGroupIds: [999],
    conditions: [],
    severity: 'info',
    message: '',
    actionable: false,
    enabled: false,
  },
];
